import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Form from './components/Form';
import PrivacyPolicy from './components/PrivacyPolicy';
import Support from './components/Support';
import IssuesList from './components/IssuesList';
import IssueDetails from './components/IssueDetails';
import IssueForm from './components/IssueForm';
import InterventionPoints from './components/InterventionPoints';
import Merch from './Merch';
import { Theme } from "@radix-ui/themes";
import { HomePage } from './pages/Home';
import AdminDashboard from './pages/AdminDashboard';
import AdminQuotas from './pages/AdminQuotas';
import AdminStocks from './pages/AdminStocks';
import AdminDeployments from './pages/AdminDeployments';
import { DeploymentDetail } from './pages/AdminDeployments/DeploymentDetail';
import Calendar from './pages/Calendar';
import SimpleAuth from './components/SimpleAuth';
import ResetPasswordPage from './pages/ResetPassword';
import * as Sentry from "@sentry/react";

import { postLoginStatus, postWebViewMessage } from './utils/appNavigation';
import { pushTokenApi } from './api/pushTokenApi';
import AppContext from './contexts/AppContext';
import { Header } from './components/Header';
import { BottomBar } from './components/BottomBar';
import AdminLayout from './layouts/AdminLayout';
import { useIssuesData } from './hooks/useHooks';
import { isInWebView } from './utils/deviceUtils';
import { Navigate } from 'react-router-dom';
import { useSessionValidation } from './hooks/useSessionValidation';
import UpdateNotification from './components/UpdateNotification';
import { useVersionCheck } from './hooks/useVersionCheck';
import { hasPermission } from './utils/permissionUtils';
import { hasRole, ROLES } from './utils/roleUtils';
import { hasUserPermission, PERMISSIONS } from './utils/permissionUtils';

import './App.css';

// Declarative configuration of admin routes with their required permissions
// Order matters: more specific routes must come first
const ADMIN_ROUTE_GUARDS = [
  { path: '/admin/quotas', permission: PERMISSIONS.DASHBOARD_QUOTAS_VIEW, fallback: '/admin' },
  { path: '/admin/stocks', permission: PERMISSIONS.DASHBOARD_STOCKS_VIEW, fallback: '/admin' },
  { path: '/admin/deployments', permission: PERMISSIONS.DASHBOARD_DEPLOYMENTS_VIEW, fallback: '/admin' },
  { path: '/admin', permission: PERMISSIONS.DASHBOARD_ACCESS, fallback: '/home' },
];

/**
 * Check permissions for an admin route and return the redirect path if needed
 * @param {string} pathname - The current route path
 * @param {Object} user - The current user with their permissions
 * @returns {string|null} - The redirect path or null if access is granted
 */
const getAdminRedirect = (pathname, user) => {
  for (const guard of ADMIN_ROUTE_GUARDS) {
    if (pathname.startsWith(guard.path) && !hasUserPermission(user, guard.permission)) {
      return guard.fallback;
    }
  }
  return null;
};

// Routes publiques qui ne nécessitent pas d'authentification
const publicRoutes = ['/privacy-policy', '/support', '/reset-password'];

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const isPublicRoute = publicRoutes.includes(location.pathname);
  const [loading, setLoading] = useState(true);
  const [userAccountId, setUserAccountId] = useState(localStorage.getItem('userAccountId'));
  const [userSectors, setUserSectors] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [userMode, setUserMode] = useState('r');
  const [userName, setUserName] = useState(localStorage.getItem('userName'));
  const [userEmail, setUserEmail] = useState(null);
  const [userServiceDeskId, setUserServiceDeskId] = useState(null);
  const [userServiceDeskName, setUserServiceDeskName] = useState('');
  const [userCreateIssueServiceDeskId, setUserCreateIssueServiceDeskId] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [serviceDesk, setServiceDesk] = useState(null);
  const serviceDeskConfig = serviceDesk?.config;
  const { isCheckingSession, validateSession } = useSessionValidation();

  // Initialize version check hook
  useVersionCheck();

  useEffect(() => {
    validateSession({
      onSuccess: (user, sd) => {
        setUserSectors(user.sectors || []);
        setUserRole(user.role || 'user');
        setUserMode(user.mode || 'r');
        setUserEmail(user.email || null);
        setUserPermissions(user.permissions || []);
        setUserServiceDeskId(sd?.id || null);
        setUserServiceDeskName(sd?.name || '');
        setUserCreateIssueServiceDeskId(user.allowed_ticket_creation_desks || []);
        setServiceDesk(sd || null);
        setLoading(false);
        Sentry.setUser({
          id: userAccountId,
          username: userName,
          email: user?.email,
        });

        const canCreateRequests = hasPermission(['technician'], user?.role)
          ? (user?.allowed_ticket_creation_desks?.length || 0) > 0
          : true;

        postLoginStatus({
          isLoggedIn: true,
          username: userName,
          logo: sd?.config?.logo,
          hasInterventionPointTab: sd?.config?.hasInterventionPointTab,
          email: user?.email,
          role: user?.role || 'user',
          canCreateRequests
        });
      },
      onError: () => {
        setUserSectors([]);
        setUserRole('user');
        setUserMode('r');
        setUserAccountId(null);
        setUserName(null);
        setUserEmail(null);
        setUserPermissions([]);
        setUserServiceDeskId(null);
        setUserServiceDeskName('');
        setUserCreateIssueServiceDeskId([]);
        setServiceDesk(null);
        setLoading(false);
        Sentry.setUser(null);
      }
    });
  }, []);

  const {
    issuesOpen,
    issuesPending,
    issuesToValidate,
    loading: issuesLoading
  } = useIssuesData({ userRole, serviceDeskConfig, maxResults: 6 });

  const handleUserSelected = () => window.location.href = '/home';

  const handleLogout = () => {
    localStorage.removeItem('userAccountId');
    localStorage.removeItem('userName');
    localStorage.removeItem('userAuthToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('selected-service-desk');
    setUserAccountId(null);
    setUserName(null);
    setUserEmail(null);
    setUserSectors([]);
    setUserRole('user');
    setUserMode('r');
    setUserPermissions([]);
    setUserServiceDeskId(null);
    setUserServiceDeskName('');

    // Clear user context in Sentry on logout
    Sentry.setUser(null);

    postLoginStatus({ isLoggedIn: false });
    navigate('/home');
  };

  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        switch (message?.type) {
          case 'logout':
            handleLogout();
            break;
          case 'checkSession':
            validateSession({
              onSuccess: (user, sd) => {
                postWebViewMessage('sessionStatus', {
                  isLoggedIn: true,
                  user: {
                    accountId: userAccountId,
                    name: userName,
                    role: user.role,
                    sectors: user.sectors,
                    serviceDeskId: sd?.id,
                    serviceDeskName: sd?.name,
                    logo: sd?.config?.logo,
                    hasInterventionPointTab: sd?.config?.hasInterventionPointTab
                  }
                });
              },
              onError: () => {
                // Send response to Expo app indicating user is not logged in
                postWebViewMessage('sessionStatus', {
                  isLoggedIn: false
                });
              }
            });
            break;

          case 'savePushToken':
            if (message.token) {
              pushTokenApi
                .register(message.token, message.platform ? `expo-${message.platform}` : undefined)
                .catch((err) => console.error('[push-token] register failed', err));
            }
            break;

          case 'unregisterPushToken':
            if (message.token) {
              pushTokenApi
                .unregister(message.token)
                .catch((err) => console.error('[push-token] unregister failed', err));
            }
            break;

          default:
            // Unknown message type
            break;
        }
      } catch (error) {
        console.error('Error parsing message from parent app:', error);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [location.pathname, userAccountId, userName, validateSession]);

  const isIssuesRoute = location.pathname.startsWith('/issues');
  const isRequestRoute = location.pathname.startsWith('/request');
  const isCalendarRoute = location.pathname.startsWith('/calendar');
  const canTechnicianAccessRequest = userCreateIssueServiceDeskId.length > 0;
  const isTechnician = hasRole(userRole, ROLES.TECHNICIAN);
  const isAdmin = hasRole(userRole, 'admin+');
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isTechnician) {
      if (isRequestRoute && !canTechnicianAccessRequest) {
        navigate('/issues', { replace: true });
      }
      else if (!isIssuesRoute && !isRequestRoute && !isCalendarRoute) {
        navigate('/issues', { replace: true });
      }
    }
  }, [isTechnician, isIssuesRoute, isRequestRoute, isCalendarRoute, canTechnicianAccessRequest, navigate]);

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );

  // Si c'est une route publique, on affiche directement le contenu
  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/support" element={<Support />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Routes>
    );
  }

  // Pour les routes privées, on vérifie l'authentification
  if (!userAccountId) {
    if (isCheckingSession) return null;
    return <SimpleAuth onAuthenticated={handleUserSelected} />;
  }

  const currentUser = {
    accountId: userAccountId,
    name: userName,
    email: userEmail,
    role: userRole,
    sectors: userSectors,
    mode: userMode,
    permissions: userPermissions,
    serviceDeskId: userServiceDeskId,
    serviceDeskName: userServiceDeskName,
    allowed_ticket_creation_desks: userCreateIssueServiceDeskId
  };

  const deviceIsInWebView = isInWebView();
  const isHomeRoute = location.pathname === '/home';
  const requestRouteElement = isTechnician && userCreateIssueServiceDeskId.length === 0
    ? <Navigate to="/issues" replace />
    : (
      <div className="page-content">
        <Form
          userAccountId={userAccountId}
          userName={userName}
          userSectors={userSectors}
          userRole={userRole}
          userMode={userMode}
          userServiceDeskId={userServiceDeskId}
          userServiceDeskName={userServiceDeskName}
          currentUser={currentUser}
          deviceIsInWebView={deviceIsInWebView}
        />
      </div>
    );

  if (isAdminRoute) {
    const adminRedirect = getAdminRedirect(location.pathname, currentUser);
    if (adminRedirect) {
      return <Navigate to={adminRedirect} replace />;
    }

    return (
      <Theme>
        <AppContext.Provider value={{ isInWebView: deviceIsInWebView, currentUser, serviceDesk }}>
          <UpdateNotification />
          <AdminLayout
            username={userName}
            email={userEmail}
            onLogout={handleLogout}
          >
            <Routes key={location.pathname}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/quotas" element={<AdminQuotas />} />
              <Route path="/admin/stocks" element={<AdminStocks />} />
              <Route path="/admin/deployments" element={<AdminDeployments />} />
              <Route path="/admin/deployments/:id" element={<DeploymentDetail />} />
            </Routes>
          </AdminLayout>
        </AppContext.Provider>
      </Theme>
    );
  }

  // Pour les routes utilisateur standard
  return (
    <Theme>
      <AppContext.Provider value={{ isInWebView: deviceIsInWebView, currentUser, serviceDesk }}>
        <UpdateNotification />
        <div className={`h-[100dvh] ${isHomeRoute ? 'overflow-y-auto' : 'flex flex-col'}`} style={{ paddingBottom: !deviceIsInWebView ? "75px" : "0px" }}>
          {!deviceIsInWebView && (
            <div className="fixed top-0 z-50 w-full">
              <Header
                username={userName}
                onLogout={handleLogout}
                logo={serviceDeskConfig?.logo}
                serviceDeskName={serviceDeskConfig?.name}
              />
            </div>
          )}
          <div className={`Wrapper w-full max-w-screen-md mx-auto px-4 ${
            isHomeRoute
              ? 'overflow-visible'
              : `flex-1 flex flex-col ${!deviceIsInWebView ? 'pt-[80px]' : ''} min-h-0`
          }`}>
            <Routes key={location.pathname}>
              <Route path="/" element={<Navigate to={isTechnician ? "/issues" : "/home"} replace />} />
              <Route path="/home" element={
                isTechnician ? <Navigate to="/issues" replace /> : (
                  <div className="page-content">
                    <HomePage
                      loading={issuesLoading}
                      issuesOpen={issuesOpen}
                      issuesPending={issuesPending}
                      issuesToValidate={issuesToValidate}
                      userName={userName}
                      userServiceDeskName={userServiceDeskName}
                      userRole={userRole}
                    />
                  </div>
                )
              } />
              <Route path="/request" element={requestRouteElement} />
              <Route path="/request/:requestTypeId" element={requestRouteElement} />
              <Route path="/calendar" element={
                isTechnician ? (
                  <div className="page-content">
                    <Calendar />
                  </div>
                ) : <Navigate to="/home" replace />
              } />
              <Route path="/issues" element={
                <div className="page-content">
                  <IssuesList
                    userAccountId={userAccountId}
                    userName={userName}
                    userSectors={userSectors}
                    userRole={userRole}
                    userMode={userMode}
                    userServiceDeskId={userServiceDeskId}
                    userServiceDeskName={userServiceDeskName}
                    currentUser={currentUser}
                  />
                </div>
              } />
              <Route path="/issues/:issueKey" element={
                <div className="page-content">
                  <IssueDetails
                    userAccountId={userAccountId}
                    userName={userName}
                    userSectors={userSectors}
                    userRole={userRole}
                    userMode={userMode}
                    userServiceDeskId={userServiceDeskId}
                    userServiceDeskName={userServiceDeskName}
                    currentUser={currentUser}
                  />
                </div>
              } />
              <Route path="/issues/:issueKey/forms/:formId" element={
                <div className="page-content">
                  <IssueForm />
                </div>
              } />
              <Route path="/intervention-points" element={
                isTechnician ? <Navigate to="/issues" replace /> : <InterventionPoints />
              } />
              <Route path="/merch" element={
                isTechnician ? <Navigate to="/issues" replace /> : (
                  <div className="page-content">
                    <Merch
                      userAccountId={userAccountId}
                      userName={userName}
                      userSectors={userSectors}
                      userRole={userRole}
                      userMode={userMode}
                      userServiceDeskId={userServiceDeskId}
                      userServiceDeskName={userServiceDeskName}
                    />
                  </div>
                )
              } />
            </Routes>
          </div>
          {!deviceIsInWebView && (
            <BottomBar
              userRole={userRole}
              serviceDesk={serviceDeskConfig}
              currentUser={currentUser}
            />
          )}
        </div>
      </AppContext.Provider>
    </Theme>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
